import React, { useEffect, useState } from 'react';
import { getSnapshot } from 'mobx-state-tree';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  ControlGroup,
  defaultColors,
  Dialog,
  FormField,
  Grid,
  Heading,
  HTMLSelect,
  InputGroup,
  majorScale,
  NumberInput,
  TagInput,
  TextArea,
  useToast,
} from '../../npaper';

import { CurrencySelect } from '../../npaper/CurrencySelect';
import {
  IProduct,
  IProductSnapshotIn,
  ProductType,
  Unit,
} from '../../stores/products/ProductStore';
import { useStores } from '../../stores/Root';
import { UnitSelect } from '../../npaper/UnitSelect';
import { FormattedMessage, useIntl } from 'react-intl';
import { CompanySelect } from '../companies/CompanySelect';
import { ProductGroupSelect } from './ProductGroupSelect';

export interface IProductDetailsDialogProps {
  isShown: boolean;
  onClose: (product?: IProduct) => void;
  product?: IProduct;
}

export const ProductDetailsDialog: React.FC<IProductDetailsDialogProps> = ({
  product,
  ...props
}) => {
  const intl = useIntl();
  const toaster = useToast();
  const [isShown, setIsShown] = useState(props.isShown);
  const [created, setCreated] = useState<IProduct>();
  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  const close = () => {
    setIsShown(false);
  };

  const { productStore, authStore } = useStores();
  const {
    register,
    control,
    setValue,
    setError,
    errors,
    handleSubmit,
    formState,
    reset,
    watch,
  } = useForm<IProductSnapshotIn & { supplier?: number | string }>();

  const productType = watch('type', ProductType.GOOD);

  useEffect(() => {
    if (!isShown) {
      return;
    }
    if (product) {
      reset(getSnapshot(product));
    } else {
      reset({
        unit: Unit.PCS,
        retail_price_currency: authStore.currentOrganisation!.default_currency, // TODO: Change this to organisation default
        own_cost_price_currency:
          authStore.currentOrganisation!.default_currency, // TODO: Change this to organisation default
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, isShown]);

  const onSubmit = async (
    values: IProductSnapshotIn & { supplier?: number | string }
  ) => {
    if (product) {
      try {
        await productStore.updateItem(product, {
          ...values,
          sku: values.sku || null,
        });
        close();
      } catch (err) {
        // TODO: Handle error
        handleError(err);
      }
    } else {
      try {
        const product = await productStore.createItem({
          ...values,
          sku: values.sku || null,
        });
        setCreated(product);
        close();
      } catch (err) {
        // TODO: Handle error
        handleError(err);
      }
    }
  };

  const handleError = (err: any) => {
    if (err.response && err.response.data && err.response.data.sku) {
      setError('sku', {
        type: 'unique',
        message: intl.formatMessage({
          defaultMessage: 'It seems there is already a product with that SKU.',
          description: 'Validation: SKU duplicate',
        }),
      });
    } else {
      toaster.showDefaultError(err);
    }
  };

  return (
    <Dialog
      title={product ? 'Rediger produkt' : 'Opret produkt'}
      isShown={isShown}
      onClose={() => props.onClose(created)}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {product ? (
              <FormattedMessage defaultMessage="Save changes" />
            ) : (
              <FormattedMessage defaultMessage="Create product" />
            )}
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <Heading size={300} className="col-span-12">
          <FormattedMessage defaultMessage="General" />
        </Heading>

        {!product && (
          <FormField
            className="col-span-9"
            label="Type"
            inline
            hint={intl.formatMessage({
              defaultMessage: 'Choose the type of your product',
            })}
          >
            <Controller
              control={control}
              name="type"
              defaultValue={ProductType.GOOD}
              render={({ onChange, value }) => (
                <HTMLSelect
                  value={value}
                  onChange={(value) => {
                    if (value === ProductType.SERVICE) {
                      setValue('unit', Unit.HOUR);
                    }
                    onChange(value);
                  }}
                  options={[
                    {
                      label: intl.formatMessage({ defaultMessage: 'Good' }),
                      value: ProductType.GOOD,
                    },
                    {
                      label: intl.formatMessage({ defaultMessage: 'Service' }),
                      value: ProductType.SERVICE,
                    },
                    {
                      label: intl.formatMessage({ defaultMessage: 'Bundle' }),
                      value: ProductType.PACKAGE,
                    },
                  ]}
                />
              )}
            />
          </FormField>
        )}
        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Name' })}
          inline
        >
          <InputGroup
            placeholder={intl.formatMessage({ defaultMessage: 'Product name' })}
            name="name"
            isInvalid={!!errors.name}
            validationMessage={errors.name && errors.name.message}
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Please provide the name of your product',
              }),
            })}
          />
        </FormField>
        <FormField className="col-span-9" label="SKU" inline>
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Stock keeping unit',
            })}
            name="sku"
            isInvalid={!!errors.sku}
            validationMessage={errors.sku && errors.sku.message}
            ref={register}
          />
        </FormField>

        {productType === ProductType.GOOD && (
          <FormField
            className="col-span-9"
            label={intl.formatMessage({
              defaultMessage: 'Manufacturer number',
            })}
            inline
          >
            <InputGroup
              placeholder={intl.formatMessage({
                defaultMessage: 'Manufacturer number (EAN, UPC, etc.)',
              })}
              name="manufacturer_number"
              isInvalid={!!errors.sku}
              validationMessage={errors.sku && errors.sku.message}
              ref={register}
            />
          </FormField>
        )}
        {!product && productType === ProductType.GOOD && (
          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Supplier' })}
            inline
            hint={intl.formatMessage({
              defaultMessage:
                'You can also choose a supplier for your product after you have created it. You can always remove the supplier after creation.',
            })}
          >
            <Controller
              control={control}
              name="supplier"
              defaultValue={null}
              render={({ onChange, value }) => (
                <CompanySelect
                  selected={value}
                  onSelect={(company) => onChange(company.id)}
                />
              )}
            />
          </FormField>
        )}

        {/* 
        <FormField className="col-span-9" label="Stregkode" inline>
          <InputGroup
            placeholder="Stregkodens værdi"
            name="barcode.value"
            ref={register}
          />
          <Controller
            control={control}
            name="barcode.type"
            defaultValue={null}
            render={({ onChange, value }) => (
              <Select
                placeholder="Vælg type på stregkode"
                onSelect={(v) => onChange(v.id)}
                value={value}
                items={Object.values(BarcodeType).map((b) => ({
                  id: b,
                }))}
              />
            )}
          />
        </FormField>
        */}

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Product group' })}
          inline
        >
          <Controller
            control={control}
            name="group"
            render={({ onChange, value }) => (
              <ControlGroup>
                <ProductGroupSelect
                  selected={value}
                  onSelect={(group) => onChange(group.id)}
                />
                {value && (
                  <Button iconLeft="cross" onClick={() => onChange(null)} />
                )}
              </ControlGroup>
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Tariff number' })}
          inline
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Tariff number',
            })}
            name="tariff_number"
            ref={register}
            maxLength={32}
          />
        </FormField>
      </Grid>

      {/** Pricing */}
      <Grid
        borderTop={`2px solid ${defaultColors.gray[100]}`}
        padding={majorScale(2)}
      >
        <Heading size={300} className="col-span-12">
          <FormattedMessage defaultMessage="Price" />
        </Heading>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Unit' })}
          inline
        >
          <Controller
            control={control}
            name="unit"
            render={({ onChange, value }) => (
              <UnitSelect
                value={value}
                onChange={onChange}
                unitDisplay="long"
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Retail price' })}
          inline
          hint={errors.retail_price && errors.retail_price.message}
        >
          <ControlGroup>
            <Controller
              control={control}
              name="retail_price"
              defaultValue={0}
              rules={{
                required: intl.formatMessage({
                  defaultMessage:
                    'Please provide a retail price for your product',
                }),
              }}
              render={({ onChange, value, onBlur }) => (
                <NumberInput
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Retail price',
                  })}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  onChange={onChange}
                  value={value}
                  onBlur={onBlur}
                />
              )}
            />
            <Controller
              control={control}
              name="retail_price_currency"
              render={({ onChange, value }) => (
                <CurrencySelect value={value} onChange={onChange} />
              )}
            />
          </ControlGroup>
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Internal cost price' })}
          inline
          hint={errors.retail_price && errors.retail_price.message}
        >
          <ControlGroup>
            <Controller
              control={control}
              name="own_cost_price"
              defaultValue={0}
              render={({ onChange, value, onBlur }) => (
                <NumberInput
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Cost price',
                  })}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                  onChange={onChange}
                  value={value}
                  onBlur={onBlur}
                />
              )}
            />
            <Controller
              control={control}
              name="own_cost_price_currency"
              render={({ onChange, value }) => (
                <CurrencySelect value={value} onChange={onChange} />
              )}
            />
          </ControlGroup>
        </FormField>
      </Grid>

      {/** Administration */}
      <Grid
        borderTop={`2px solid ${defaultColors.gray[100]}`}
        padding={majorScale(2)}
      >
        <Heading size={300} className="col-span-12">
          <FormattedMessage defaultMessage="Administration" />
        </Heading>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Reference' })}
          inline
        >
          <InputGroup
            placeholder={intl.formatMessage({ defaultMessage: 'A reference' })}
            name="reference"
            ref={register}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Description' })}
          inline
        >
          <TextArea
            placeholder={intl.formatMessage({
              defaultMessage: 'A short description of the product',
            })}
            name="description"
            ref={register}
            maxLength={500}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Tags' })}
          inline
        >
          <Controller
            control={control}
            name="tags"
            defaultValue={[]}
            render={({ onChange, value, onBlur }) => (
              <TagInput value={value} onChange={onChange} onBlur={onBlur} />
            )}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};
